import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Search, FileText, Clock, CheckCircle, XCircle, Download, Eye, Send, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Complete', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejete', class: 'bg-red-100 text-red-700', icon: XCircle },
};

const requestTypeLabels = {
  devis: 'Demande de Devis',
  facture: 'Demande de Facture',
  contrat: 'Demande de Contrat',
  rh_contrat: 'Contrat RH',
};

const roleLabels = {
  commercial: 'Commercial',
  rh: 'RH',
};

export const AdminDocuClient = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRespondOpen, setIsRespondOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  
  const [responseComment, setResponseComment] = useState('');
  const [responseFile, setResponseFile] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API}/admin/docu-client`, { credentials: 'include' });
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (requestId, fileType, filename) => {
    setDownloading(`${requestId}-${fileType}`);
    try {
      const response = await fetch(`${API}/docu-client/${requestId}/download/${fileType}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${fileType}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Fichier telecharge');
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const openDetail = (req) => {
    setSelectedRequest(req);
    setIsDetailOpen(true);
  };

  const openRespond = (req) => {
    setSelectedRequest(req);
    setResponseComment('');
    setResponseFile(null);
    setIsRespondOpen(true);
  };

  const openReject = (req) => {
    setSelectedRequest(req);
    setRejectReason('');
    setIsRejectOpen(true);
  };

  const handleRespond = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (responseComment) formData.append('comment', responseComment);
      if (responseFile) formData.append('response_file', responseFile);

      const response = await fetch(`${API}/admin/docu-client/${selectedRequest.request_id}/respond`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Reponse envoyee');
        setIsRespondOpen(false);
        setIsDetailOpen(false);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Respond error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Le motif est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('reason', rejectReason);

      const response = await fetch(`${API}/admin/docu-client/${selectedRequest.request_id}/reject`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Demande rejetee');
        setIsRejectOpen(false);
        setIsDetailOpen(false);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      req.requester_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.raison_sociale?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="admin-docu-client-title">
          Demandes de documents
        </h1>
        <p className="text-slate-500 mt-1">Gerez les demandes de devis, factures et contrats</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-xl font-bold text-slate-900">{requests.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En attente</p>
              <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Completees</p>
              <p className="text-xl font-bold text-emerald-600">
                {requests.filter(r => r.status === 'completed').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Rejetees</p>
              <p className="text-xl font-bold text-red-600">
                {requests.filter(r => r.status === 'rejected').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par demandeur ou client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="completed">Completees</SelectItem>
            <SelectItem value="rejected">Rejetees</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Demandeur</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Aucune demande
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((req) => {
                  const StatusIcon = statusConfig[req.status]?.icon || Clock;
                  return (
                    <TableRow key={req.request_id} className="hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{req.requester_name}</p>
                          <Badge variant="outline" className="text-xs mt-1">
                            {roleLabels[req.requester_role] || req.requester_role}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                          {requestTypeLabels[req.request_type] || req.request_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{req.raison_sociale || '-'}</TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(req.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[req.status]?.class} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[req.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openDetail(req)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {req.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openRespond(req)}
                                className="text-emerald-600 hover:bg-emerald-50"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openReject(req)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Details de la demande</DialogTitle>
            <DialogDescription>
              {selectedRequest && requestTypeLabels[selectedRequest.request_type]} par {selectedRequest?.requester_name}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Raison sociale</p>
                  <p className="font-medium">{selectedRequest.raison_sociale || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Nom commercial</p>
                  <p className="font-medium">{selectedRequest.nom_commercial || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">SIRET</p>
                  <p className="font-medium">{selectedRequest.siret || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">TVA Intra</p>
                  <p className="font-medium">{selectedRequest.tva_intra || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Emails</p>
                  <p className="font-medium">{selectedRequest.emails?.join(', ') || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Adresse facturation</p>
                  <p className="font-medium">{selectedRequest.adresse_facturation || '-'}</p>
                </div>
              </div>

              {/* Bank Info */}
              {selectedRequest.iban && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-3">Coordonnees bancaires</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">IBAN</p>
                      <p className="font-medium">{selectedRequest.iban}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">BIC</p>
                      <p className="font-medium">{selectedRequest.bic || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Titulaire</p>
                      <p className="font-medium">{selectedRequest.titulaire || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Offer Info */}
              {(selectedRequest.montant != null || selectedRequest.duree_annees != null || selectedRequest.duree_mois != null || selectedRequest.offre) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-3">Montant et duree</h4>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Montant TTC</p>
                      <p className="font-medium">
                        {selectedRequest.montant != null
                          ? `${Number(selectedRequest.montant).toFixed(2)} EUR`
                          : (selectedRequest.offre || '-')}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Duree</p>
                      <p className="font-medium">
                        {(selectedRequest.duree_annees != null || selectedRequest.duree_mois != null)
                          ? `${selectedRequest.duree_annees || 0} an(s) ${selectedRequest.duree_mois || 0} mois`
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Echelonnement</p>
                      <p className="font-medium">{selectedRequest.echelonnement || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Remise</p>
                      <p className="font-medium">{selectedRequest.remise_commerciale || 0}%</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Comments */}
              {selectedRequest.commentaires && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-2">Commentaires</h4>
                  <p className="text-sm text-slate-600">{selectedRequest.commentaires}</p>
                </div>
              )}

              {/* Attached Files */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Fichiers joints</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.devis_signe_filename && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadFile(selectedRequest.request_id, 'devis_signe', selectedRequest.devis_signe_filename)}
                      disabled={downloading === `${selectedRequest.request_id}-devis_signe`}
                    >
                      {downloading === `${selectedRequest.request_id}-devis_signe` ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                      Devis signe
                    </Button>
                  )}
                  {selectedRequest.contrat_signe_filename && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => downloadFile(selectedRequest.request_id, 'contrat_signe', selectedRequest.contrat_signe_filename)}
                      disabled={downloading === `${selectedRequest.request_id}-contrat_signe`}
                    >
                      {downloading === `${selectedRequest.request_id}-contrat_signe` ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                      Contrat signe
                    </Button>
                  )}
                  {!selectedRequest.devis_signe_filename && !selectedRequest.contrat_signe_filename && (
                    <p className="text-sm text-slate-500">Aucun fichier joint</p>
                  )}
                </div>
              </div>

              {/* Status Badge */}
              <div className="border-t pt-4">
                <Badge className={`${statusConfig[selectedRequest.status]?.class} border`}>
                  {statusConfig[selectedRequest.status]?.label}
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button variant="outline" onClick={() => openReject(selectedRequest)} className="text-red-600">
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button onClick={() => openRespond(selectedRequest)}>
                  <Send className="w-4 h-4 mr-2" />
                  Repondre
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog open={isRespondOpen} onOpenChange={setIsRespondOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Repondre a la demande</DialogTitle>
            <DialogDescription>
              Envoyez le document demande et/ou un commentaire
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Commentaire (facultatif)</Label>
              <Textarea
                value={responseComment}
                onChange={(e) => setResponseComment(e.target.value)}
                placeholder="Votre message..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Document a envoyer (facultatif)</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setResponseFile(e.target.files[0])}
                  className="hidden"
                  id="response-upload"
                />
                <label htmlFor="response-upload" className="cursor-pointer">
                  {responseFile ? (
                    <>
                      <FileText className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                      <p className="font-medium text-emerald-600">{responseFile.name}</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                      <p className="text-slate-500">Cliquez pour telecharger</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRespondOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleRespond} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter la demande</DialogTitle>
            <DialogDescription>
              Indiquez le motif du rejet
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motif du rejet..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleReject} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
